/**
 * HookStage Component
 * Container for hooks in a specific stage (system/user/final)
 */

import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { HookItem } from './HookItem';
import type { HookConfig, HookStage as HookStageType } from '../../types/hooks';
import type { HooksMessages } from '@/i18n/messages';

interface HookStageProps {
  stage: HookStageType;
  hooks: HookConfig[];
  selectedHook: HookConfig | null;
  isDragging: boolean;
  onSelectHook: (hook: HookConfig) => void;
  onToggleVisibility: (hook: HookConfig) => void;
  translations: HooksMessages;
}

const STAGE_ICONS = {
  system: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
      />
    </svg>
  ),
  user: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  ),
  final: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>
  ),
};

export const HookStage: React.FC<HookStageProps> = ({
  stage,
  hooks,
  selectedHook,
  isDragging,
  onSelectHook,
  onToggleVisibility,
  translations: t,
}) => {
  const { setNodeRef } = useDroppable({
    id: `stage-${stage}`,
  });

  const icon = STAGE_ICONS[stage];
  const stageTitle = t.stages[stage];
  const sortedHooks = [...hooks].sort((a, b) => a.sort_order - b.sort_order);
  const hookIds = sortedHooks.map((h) => `hook-${stage}-${h.hook_name}`);

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-gray-600">{icon}</div>
        <h4 className="font-medium">{stageTitle}</h4>
        <span className="text-xs text-gray-500">({t.hooksCount.replace('{count}', String(hooks.length))})</span>
      </div>

      <div ref={setNodeRef} id={`hook-stage-${stage}`} data-stage={stage}>
        <SortableContext items={hookIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sortedHooks.map((hook) => (
              <HookItem
                key={hook.hook_name}
                hook={hook}
                isSelected={selectedHook?.hook_name === hook.hook_name}
                isDragging={isDragging}
                onSelect={() => onSelectHook(hook)}
                onToggleVisibility={() => onToggleVisibility(hook)}
              />
            ))}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};
